import React from 'react';
import PropTypes from 'prop-types';
import Flexbox from 'flexbox-react';
import { Button, Menu } from 'antd';
import { Modal } from 'react-bootstrap';
import { css, cx } from 'emotion';
import ComboBoxHOC from 'components/core/ComboBoxHOC';
import DatastreamVarsList from 'components/page/process/variableRetrieval/DatastreamVarsList';
import RecommendedVariablesList from 'components/page/process/variableRetrieval/RecommendedVariablesList';
import Constants from 'utils/Constants';
import ToolbarItem from 'components/core/toolbar/ToolbarItem';
import InfoMessages from 'components/core/help/InfoMessages';
import ModalsService from 'services/ModalsService';
import DodService from 'services/DodService';
import ProcessValidator from 'utils/ProcessValidator';
import ComponentUtils from 'utils/ComponentUtils';
import ProcessUtils from 'utils/ProcessUtils';
import NavigationUtils from 'utils/NavigationUtils';
import InfoIcon from 'components/core/InfoIcon';
import InfoBoxLabel from 'components/core/InfoBoxLabel';
import SecondsInput from 'components/core/editable/SecondsInput';
import ImportMappingsTable from 'components/page/process/variableRetrieval/ImportMappingsTable';
import LoadingSpinner from 'components/core/LoadingSpinner';
import VariableRequiredOptionsForm from 'components/core/forms/VariableRequiredOptionsForm';
import _ from 'lodash';
import $ from 'jquery';
import { colorTextGrey, colorRed } from 'styles/base';
import RunLocation from 'utils/RunLocation';

const dodUrl = process.env.REACT_APP_DOD_URL; // injected by Node.js build environment parameters

const typeSelectionStyle = css`
    margin: 0 -15px 15px -15px;
    padding: 0 15px 15px 15px;
    border-bottom: 1px solid #ddd;
`;

export default class ImportVariablesWizard extends React.Component {
    static propTypes = {
        process: PropTypes.object,
        dods: PropTypes.array,
        dodsById: PropTypes.object,
        siteFacilities: PropTypes.array,
        recommendedVariables: PropTypes.array,
        wizardView: PropTypes.bool,
        variableRequired: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]).isRequired,
        retrieveQcVariable: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]).isRequired,
        qcVariableRequired: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]).isRequired,
    };

    static defaultProps = {
        wizardView: false,
        variableRequired: false,
        retrieveQcVariable: true,
        qcVariableRequired: false,
        mode: Constants.RETRIEVE_MODE_DS
    };

    constructor(props) {
        super(props);

        // Initialize State
        this.state = {
            selectedDod: null,
            variableRequired: this.props.variableRequired,
            retrieveQcVariable: this.props.retrieveQcVariable,
            qcVariableRequired: this.props.qcVariableRequired,
            endOffset: 0,
            startOffset: 0,
            coordinateSystem: null,
            variableDimensions: {},
            inputDataset: Constants.INPUT_DATASET_AUTO,
            outputDatastreams: [],
            selectedVars: null,
            variableMappings: {},
            prefix: '',
            buttonDisabled: true,
            mode: props.mode,
            selectedRecVar: null,
        };
        this._initializePropDependentVariables(props);
    }

    componentWillReceiveProps = (nextProps) => {
        // Every time our props change, we need to refresh our input dataset, because the process has changed
        this._initializePropDependentVariables(nextProps);
    };

    componentWillUpdate(nextProps, nextState) {
        if (nextState.selectedDod) {
            // We have to fetch datastream outside of the render method or it throws an error
            DodService.fetchDodVarsIfNeeded(nextState.selectedDod);
        } else if (nextState.selectedRecVar) {
            nextProps.recommendedVariables.forEach(variable => {
                if (variable.var_name === nextState.selectedRecVar) {
                    const dsName = `${variable.instrument_code}.${variable.data_level_code}`;
                    DodService.fetchDodVarsIfNeeded(dsName);
                }
            })
        }
    }

    _initializePropDependentVariables(props) {
        this.canCommit = false;
        this.dodSelectOptions = Constants.convertArrayToSelectOptions(props.dods);

        const recommendedVars = props.recommendedVariables.reduce(function(filtered, option) {
            if (filtered.indexOf(option.var_name) === -1) {
                filtered.push(option.var_name);
            }
            return filtered;
        }, []);

        this.recVarSelectOptions = Constants.convertArrayToSelectOptions(recommendedVars);
        this.coordinateSystemOptions = ProcessUtils.getCoordinateSystemOptionsForVariable(props.process);
        this.outputDatastreamOptions = ProcessUtils.getOutputDatstreamOptionsForVariable(props.process);
    }

    getInputDatasetOptions = () => {
        const auto = [Constants.INPUT_DATASET_AUTO];
        const existingDatasets = Object.keys(this.props.process.variable_retrieval.input_datasets);
        let inputDatasets = _.concat(auto, existingDatasets);

        // If the current input dataset name (which user typed in to create new) is not in the
        // default list, then add it.
        if ( !_.includes(inputDatasets, this.state.inputDataset)) {
            inputDatasets = _.concat(this.state.inputDataset, inputDatasets);
        }
        return Constants.convertArrayToSelectOptions(inputDatasets);
    };

    getDodVars = () => {
        let dodVars = null;

        if (this.state.selectedDod) {
            const dodVariables = ProcessUtils.getDatastreamVariablesForDod(this.state.selectedDod);
            if (dodVariables) {
                dodVars = {};
                Object.keys(dodVariables).forEach(variableName => {
                    if (Object.keys(this.state.variableMappings).indexOf(variableName) === -1) { // get rid of vars that are already in the mappings table
                        dodVars[variableName] = dodVariables[variableName];
                    }
                });
            }

        } else {
            dodVars = {};
        }

        return dodVars;
    };

    getRecommendedVariables = () => {
        return this.props.recommendedVariables.filter(variable => {
            if (this.state.selectedDod == null) {
                return true;
            }

            if (this.state.selectedDod == variable.dod) {
                return true;
            }
        });
    };

    getDefaultPrefix = (datastreamName) => {
        let prefix = '';

        //only set default automatically on simple view of wizard
        if (this.props.wizardView) {
            const segs = datastreamName.split('.');
            const datastreamClass = segs[0];
            //const datastreamLevel = segs[1];

            prefix = `${datastreamClass}_`;
        }

        //variable names must start with a letter so strip off any numbers from start of datastream name
        let i = 0;
        for (i; i < prefix.length; i++) {
            if (isNaN(prefix.charAt(i))) {
                break;
            }
        }
        if (i > 0) {
            prefix = prefix.substr(i, prefix.length - i);
        }

        return prefix;
    };

    getPrefixedValue = (prefix, value) => {
        if (prefix !== '') {
            return `${prefix}${value}`;
        } else {
            return value;
        }
    };

    onModeChange = (e) => {
        this.clearErrorMessage();
        let newState = { mode: e.key, buttonDisabled: true, prefix: '', selectedDod: null, selectedRecVar: null, selectedVars: null, variableMappings: {}};
        this.setState(newState);
    };

    handleInputChange = (event) => {
        const target = event.target;
        const value = target.value;
        const name = target.name;

        this.setState({
            [name]: value
        });
    };

    onVariableRequiredChanged = (value) => {
        this.setState({ variableRequired: value });
    };

    onRetrieveQCVariableChanged = (value) => {
        this.setState({ retrieveQcVariable: value });
    };

    onQCVariableRequiredChanged = (value) => {
        this.setState({ qcVariableRequired: value });
    };

    handleComponentInputChange = (name, value) => {
        this.setState({
            [name]: value
        });
    };

    onCoordinateSystemChange = (e) => {
        let value = null;
        if (e !== null) {
            value = e.value;
        }
        this.setState({
            coordinateSystem: value
        });
    };

    onOutputDatastreamChange = (e) => {
        this.clearErrorMessage();

        const outputDatastreams = e.map((selection) => {
            return selection.value;
        });

        const variableMappings = this.updateOutputMappings(outputDatastreams);
        this.setState({ outputDatastreams, variableMappings });
    };

    onInputDatasetChange = (e) => {
        let value = null;
        if (e !== null) {
            value = e.value;
        }
        this.setState({
            inputDataset: value
        });
    };

    onDatastreamChange = (selectedValue) => {
        this.clearErrorMessage();
        let newState = { buttonDisabled: true };
        if (selectedValue) {
            const selectedDod = selectedValue ? selectedValue.value : null;

            if (selectedDod !== this.state.selectedDod) {
                const prefix = this.getDefaultPrefix(selectedDod);
                newState = Object.assign(newState, { variableMappings: {}, prefix });
            }
            newState = Object.assign(newState, { selectedDod, selectedVars: null });
        } else {
            newState = Object.assign(newState, { selectedDod: null });
        }
        this.setState(newState);
    };

    onDatastreamVarsChange = (selectedItems, lastSelectedItem) => {
        if (this.errorMessageComponent !== null) {
            this.clearErrorMessage();
        }
    };

    onRecDatastreamChange = (selectedItems, lastSelectedItem) => {
        if (this.errorMessageComponent !== null) {
            this.clearErrorMessage();
        }
    };

    onRetrievedVariableChanged = (key, value) => {
        const newMappingState = _.cloneDeep(this.state.variableMappings);
        newMappingState[key].retrievedVariableName = value;
        this.clearErrorMessage();
        this.setState({ variableMappings: newMappingState });
    };

    onVariableUnitsChanged = (key, value) => {
        const newMappingState = _.cloneDeep(this.state.variableMappings);
        newMappingState[key].units = value;
        this.clearErrorMessage();
        this.setState({ variableMappings: newMappingState });
    };

    onOutputMappingChanged = (key, value) => {
        const newMappingState = _.cloneDeep(this.state.variableMappings);
        newMappingState[key].outputMapping = value;
        this.clearErrorMessage();
        this.setState({ variableMappings: newMappingState });
    };

    addMappingToState = (newVariables) => {
        let newMappingState = _.cloneDeep(this.state.variableMappings);
        let newDimensionsState = _.clone(this.state.variableDimensions);

        const dodVars = this.getDodVars();
        newVariables.forEach((name) => {
            const retrievedVariableName = this.getPrefixedValue(this.state.prefix, name);
            let units = Constants.SAME_AS_INPUT_DATASTREAM;

            if (dodVars[name]) {
                if (dodVars[name].units) {
                    units = dodVars[name].units;
                }
                if (dodVars[name].dims) {
                    dodVars[name].dims.forEach(dimensionName => {
                        if (!newDimensionsState[dimensionName]) {
                            newDimensionsState = Object.assign({}, newDimensionsState, {
                                [dimensionName]: dimensionName
                            });
                        }
                    });
                }
            }

            let outputMapping = this.getOutputMappingName(name, retrievedVariableName);

            newMappingState = Object.assign({}, newMappingState, {
                [name]: { retrievedVariableName, units, outputMapping }
            });
        });

        this.setState({ variableMappings: newMappingState, variableDimensions: newDimensionsState });
    };

    addRecommendedVariableMappingToState = (selectedValues) => {
        let newMappingState = _.cloneDeep(this.state.variableMappings);
        let newDimensionsState = _.clone(this.state.variableDimensions);

        let datastreamName = null;
        const newVariables = [];
        selectedValues.forEach(value => {
            const selection = value.split('-');
            newVariables.push(selection[0]);
            datastreamName = selection[1];
        })
        
        if (datastreamName) {
            const dodVars = ProcessUtils.getDatastreamVariablesForDod(datastreamName);

            newVariables.forEach(name => {
                
                const retrievedVariableName = this.getPrefixedValue(this.state.prefix, name);
                let units = Constants.SAME_AS_INPUT_DATASTREAM;
    
                if (dodVars && dodVars[name]) {
                    if (dodVars[name].units) {
                        units = dodVars[name].units;
                    }
                    if (dodVars[name].dims) {
                        dodVars[name].dims.forEach(dimensionName => {
                            if (!newDimensionsState[dimensionName]) {
                                newDimensionsState = Object.assign({}, newDimensionsState, {
                                    [dimensionName]: dimensionName
                                });
                            }
                        });
                    }
                }
    
                let outputMapping = this.getOutputMappingName(name, retrievedVariableName);
                newMappingState = Object.assign({}, newMappingState, {
                    [name]: { retrievedVariableName, units, outputMapping }
                });
            })
            
        }

        this.setState({ selectedDod: datastreamName, variableMappings: newMappingState, variableDimensions: newDimensionsState });
    };

    onAddMappingFromListSelection = () => {
        this.clearErrorMessage();
        if (this.state.mode === Constants.RETRIEVE_MODE_DS && this.datastreamVarsComponent) {
            this.addMappingToState(Object.keys(this.datastreamVarsComponent.getSelectedItems()));
            this.datastreamVarsComponent.clearSelection();
        }

        if (this.state.mode === Constants.RETRIEVE_MODE_REC && this.datastreamRecComponent) {
            console.log(this.datastreamRecComponent.refs.filterInput);
            this.datastreamRecComponent.setFilter('');
            this.addRecommendedVariableMappingToState(Object.keys(this.datastreamRecComponent.getSelectedItems()));
        }
    };

    onAddMapping = () => {
        this.clearErrorMessage();

        const isVariableValid = (names, checkUnique) => {
            const existingList = Object.keys(this.state.variableMappings);
            let result = null;
            names.forEach((name) => {
                //TODO: this is just doing the last one at the moment
                result = new ProcessValidator(this.props.process).isEntityNameValid(name, existingList, 'Variable');
            });

            return result;
        };

        let dods = [];
        if (this.getDodVars()) {
            dods = Object.keys(this.getDodVars());
        }

        ComponentUtils.openNewEntityModal('Import new variables',
            (newNames) => {
                this.addMappingToState(newNames);
            },
            isVariableValid,
            {
                options: Constants.sortSelectOptions(Constants.convertArrayToSelectOptions(dods)),
                inputLabel: 'Variable from file',
                newSelectionPrefix: 'New variable...',
                multi: true
            });
    };

    onDeleteMapping = (selectedItems) => {
        const deleteOk = () => {
            const newMappingState = this.state.variableMappings;
            Object.keys(selectedItems).forEach((name) => {
                delete newMappingState[name];
            });

            let selectedDod = this.state.selectedDod;
            if (this.state.mode == Constants.RETRIEVE_MODE_REC && Object.keys(newMappingState).length === 0) {
                selectedDod = null;
            }

            this.setState({ variableMappings: newMappingState, selectedDod });
            this.selectedDatastreamVarsRefFunction.clearSelection();

            // if they delete the variable with the error
            this.clearErrorMessage();
        };

        const list = Object.keys(selectedItems).map((variable) => {
            return <li key={variable}>{variable}</li>;
        });

        const message = <ul>{list}</ul>;

        ModalsService.openModal({
            id: 'confirm_remove_mapping',
            modalType: ModalsService.TYPE_CONFIRM,
            modalTitle: 'Confirm Delete',
            modalMessage: message,
            onOk: deleteOk
        });
    };

    updatePrefixMappings = (newPrefix) => {
        const oldPrefix = this.state.prefix;
        const newMappings = _.clone(this.state.variableMappings);
        if (oldPrefix !== '') {
            Object.keys(newMappings).forEach((key) => {
                const mappingValue = newMappings[key];
                if (mappingValue.retrievedVariableName.indexOf(oldPrefix) === 0) {
                    newMappings[key].retrievedVariableName = mappingValue.retrievedVariableName.replace(oldPrefix, newPrefix);
                }

                newMappings[key].outputMapping = this.getOutputMappingName(key, newMappings[key].retrievedVariableName);
            });
        } else {
            Object.keys(newMappings).forEach((key) => {
                newMappings[key].retrievedVariableName = this.getPrefixedValue(newPrefix, newMappings[key].retrievedVariableName);
                newMappings[key].outputMapping = this.getOutputMappingName(key, newMappings[key].retrievedVariableName);
            });
        }

        return newMappings;
    };

    updateOutputMappings = (outputDatastreams) => {
        const newMappings = _.clone(this.state.variableMappings);
        Object.keys(newMappings).forEach((key) => {
            newMappings[key].outputMapping = this.getOutputMappingName(key, newMappings[key].retrievedVariableName, outputDatastreams);
        });

        return newMappings;
    };

    getOutputMappingName = (datastreamVariable, retrievedVariableName, outputDatastreams = null) => {
        outputDatastreams = outputDatastreams ? outputDatastreams : this.state.outputDatastreams;

        let name = retrievedVariableName;
        if (datastreamVariable !== retrievedVariableName) {
            outputDatastreams.forEach(datastreamName => {
                const datastreamVariables = ProcessUtils.getDatastreamVariablesForDod(datastreamName);
                if (datastreamVariables && Object.keys(datastreamVariables).indexOf(datastreamVariable) > -1) {
                    name = datastreamVariable;
                }
            });
        }

        return name;
    };

    onPrefixChange = () => {
        const isPrefixValid = (prefix) => {
            return new ProcessValidator(this.props.process).isVariablePrefixValid(prefix);
        };

        ComponentUtils.openNewEntityModal('prefix to variable names',
            (prefix) => {
                this.clearErrorMessage();
                //we automatically add an underscore, so remove one from the end if they added one
                if (prefix === null) {
                    prefix = '';
                } else if (prefix.lastIndexOf('_') !== prefix.length - 1) {
                    prefix = `${prefix}_`;
                }

                const variableMappings = this.updatePrefixMappings(prefix);
                this.setState({ prefix, variableMappings });
            },
            isPrefixValid,
            {
                inputLabel: 'Prefix'
            });
    };

    /**
     *
     * @param existingVariables - These are variables that already exist, so if this is set, we are appending a new
     * rule to the input dataset for the existing variables.
     */
    commitChanges = (existingVariables) => {
        const mode = ProcessUtils.getProcessUIState(this.props.process).mode;
        const editor = ProcessUtils.getEditor(this.props.process);
        const retrievedVariablesList = editor.process.variable_retrieval.retrieved_variables;

        // The mapping from datastream variable to retrieved variable as set in the form
        const formVariableMappings = _.clone(this.state.variableMappings);
        const dodVars = ProcessUtils.getDatastreamVariablesForDod(this.state.selectedDod);

        let newDatasetName = null;
        if (this.state.inputDataset == Constants.INPUT_DATASET_AUTO) {
            newDatasetName = this.createInputDatasetName(this.state.selectedDod);
        } else {
            newDatasetName = this.state.inputDataset
        }

        const inputDatasetsToEdit = {}; //input datasets that are having the new rule appended to them
        const retrievedVariablesToEdit = {};
        const outputMappings = {};

        // For a particular dataset name, retrieved variable, and mapped datastream variable combination, add a
        // new dataset to be created or append to an existing one
        const queueInputDatasetToEdit = (inputDatasetName, retrievedVariableName, datastreamVariableName) => {

            const inputDatasetEditInformation = inputDatasetsToEdit[inputDatasetName];

            if (!inputDatasetEditInformation) {
                const inputDataset = this.props.process.variable_retrieval.input_datasets[inputDatasetName];
                let priority = 1;
                let offsets = [this.state.startOffset, this.state.endOffset];
                if (inputDataset) {
                    priority = inputDataset.rules.length + 1;
                    offsets = inputDataset.offsets;
                }

                const rule = _.cloneDeep(Constants.INPUT_DATASET_RULE_NEW);
                rule.datastream_name = this.state.selectedDod;
                rule.priority = priority;

                // Now guess the variable mappings for the new rule.  If adding this rule to an existing rule set,
                // then we have to try to find the correct datastream variable name for all the other variables in
                // the input dataset.
                const mappings = {};
                const retrievedVariablesForDataset = ProcessUtils.getRetrievedVariablesForDataset(this.props.process, inputDatasetName);

                retrievedVariablesForDataset.forEach(assignedVariableName => {
                    const datastreamVariableNames = ProcessUtils.guessInputDatastreamVariableNames(this.props.process, inputDatasetName, assignedVariableName, this.state.selectedDod);
                    mappings[assignedVariableName] = datastreamVariableNames;
                });

                // Always assign the current variable name to the current datastream variable name
                mappings[retrievedVariableName] = [datastreamVariableName];

                // add prior names after in mappings list
                let priorNames = [];
                if (dodVars[datastreamVariableName]) {
                    priorNames = dodVars[datastreamVariableName].renamed;
                }
                if (priorNames && priorNames.length > 0) {
                    mappings[retrievedVariableName] = [...mappings[retrievedVariableName], ...priorNames];
                }

                rule.variable_mappings = mappings;


                inputDatasetsToEdit[inputDatasetName] = {
                    offsets,
                    rule
                };

            } else {
                // We have already added this dataset to our map, so we need to add the variable
                // mapping to the new rule being created
                let mappings = [datastreamVariableName];

                // add prior names after in mappings list
                let priorNames = [];
                if (dodVars[datastreamVariableName]) {
                    priorNames = dodVars[datastreamVariableName].renamed;
                }
                if (priorNames && priorNames.length > 0) {
                    mappings = [...mappings, ...priorNames];
                }

                inputDatasetEditInformation.rule.variable_mappings[retrievedVariableName] = mappings;
            }
        };

        const queueRetrievedVariableToEdit = (retrievedVariableName, units, datastreamVariableName) => {
            if (!retrievedVariablesList[retrievedVariableName]) {

                // default values for retrieved variable
                let dataType = 'float';
                let dims = [];

                if (dodVars) {
                    if (dodVars[datastreamVariableName]) {
                        dataType = dodVars[datastreamVariableName].type;
                        dims = dodVars[datastreamVariableName].dims;
                    }
                }

                dims = dims.map(dimension => {
                    return this.state.variableDimensions[dimension];
                });

                // We need to add the retrieved variable after we check the existing vars, because it may have been renamed
                retrievedVariablesToEdit[retrievedVariableName] = {
                    name: retrievedVariableName,
                    data_type: dataType,
                    units,
                    variable_required: this.state.variableRequired ? 1 : 0,
                    retrieve_qc_variable: this.state.retrieveQcVariable ? 1 : 0,
                    qc_variable_required: this.state.qcVariableRequired ? 1 : 0,
                    input_dataset: newDatasetName, // any new retrieved variables will always go to the new input dataset
                    coordinate_system: null,
                    dims: Object.assign([], dims)
                };

            } else {
                retrievedVariablesToEdit[retrievedVariableName] = null;
            }
        };

        // Loop through all the variables in the form, queuing them up for final edit
        for (const datastreamVariable in formVariableMappings) {
            // This is the retrievedVariable associated with the mapping - it may already exist
            let retrievedVariableName = formVariableMappings[datastreamVariable].retrievedVariableName;
            const units = formVariableMappings[datastreamVariable].units;
            let inputDatasetNameToQueue = newDatasetName;

            if (existingVariables) { // some of the selected variables may already exist
                if (Object.keys(existingVariables).indexOf(retrievedVariableName) > -1) { // this is an existing variable

                    if (existingVariables[retrievedVariableName].action === 'addRule') { // we are going to add a new rule to this existing datastream
                        inputDatasetNameToQueue = retrievedVariablesList[retrievedVariableName].input_dataset;

                    } else {
                        retrievedVariableName = existingVariables[retrievedVariableName].rename;
                    }
                }
            }

            outputMappings[retrievedVariableName] = formVariableMappings[datastreamVariable].outputMapping;

            // Queue the proper input dataset and variable mapping for addition
            queueInputDatasetToEdit(inputDatasetNameToQueue, retrievedVariableName, datastreamVariable);

            queueRetrievedVariableToEdit(retrievedVariableName, units, datastreamVariable);
        }

        // Edit all the affected input datasets
        Object.keys(inputDatasetsToEdit).forEach((inputDatasetName) => {
            const editInfo = inputDatasetsToEdit[inputDatasetName];

            // We have to do a special check for met.b1 and add the E13 companion rule
            // Note that companion rules need to be added before the new rule since they would be
            // higher priority
            this.addSGPCompanionRules(editor, inputDatasetName, editInfo);

            editor.editInputDataset(inputDatasetName, editInfo.offsets, editInfo.rule);

        });

        // Edit all the affected retrieved variables
        // We must do this BEFORE we edit the output datastreams to make sure all the new variables exist
        Object.values(retrievedVariablesToEdit).forEach(retrievedVariable => {
            if (retrievedVariable !== null) {
                editor.process.variable_retrieval.retrieved_variables[retrievedVariable.name] = retrievedVariable;

                if (this.state.inputDataset !== Constants.INPUT_DATASET_AUTO) {
                    editor.updateInputDatasetMappingByVariable(this.state.inputDataset, retrievedVariable.name);
                }
            }
        });

        // Edit all the affected output datastreams
        if (this.state.outputDatastreams !== null) {
            this.state.outputDatastreams.forEach((outputDatastreamName) => {
                editor.addOutputDatastream(outputDatastreamName);
                Object.keys(retrievedVariablesToEdit).forEach(retrievedVariableName => {
                    editor.addOutputDatastreamToVariable(outputDatastreamName, retrievedVariableName);
                    editor.setOutputDatastreamMappingForVariable(retrievedVariableName, outputDatastreamName, outputMappings[retrievedVariableName]);
                });
            });
        }

        // In this section we decide if we need to merge in the retrieval rule that we just created into another
        // rule in either the same dataset or a different dataset.
        // TODO: this logic is confusing to me - see if we can refactor to make it more clear
        if (this.state.inputDataset == Constants.INPUT_DATASET_AUTO) {
            // If we are using AUTO_SELECT, then we know the wizard will always create a brand new
            // dataset with one rule to put these variables in. In this case, we need to check if the rule for the
            // new dataset is already present in another dataset, if so, then we will move the
            // variables to the other dataset and delete the new one that was created.
            editor.mergeRulesToOtherDatasets(newDatasetName);

        } else {
            // If the user specified a specific input dataset name, then just merge rules within that dataset
            editor.mergeLastRuleCreatedInSameDataset(this.state.inputDataset);
        }
        editor.syncInputStreams();

        if (mode === 'wizard') {
            // create output datastream and coordinate system if they don't already exist
            if (editor.process.output_datastreams.length === 0) {
                const defaultOutputName = ProcessUtils.getDefaultOutputDatastreamName(editor.process);
                editor.addOutputDatastream(defaultOutputName);
            }

            const outputDatastreamName = editor.process.output_datastreams[0];
            Object.keys(retrievedVariablesToEdit).forEach(retrievedVariableName => {
                editor.addOutputDatastreamToVariable(outputDatastreamName, retrievedVariableName);
            });

            if (Object.keys(editor.process.variable_retrieval.coordinate_systems).length === 0) {
                editor.addCoordinateSystem(Constants.DEFAULT_COORDINATE_SYSTEM_NAME);
            }

            // only one coordinate system in wizard mode
            const coordinateSystemName = Object.keys(editor.process.variable_retrieval.coordinate_systems)[0];
            const retrievedVariableNames = Object.keys(retrievedVariablesToEdit);
            editor.addRetrievedVariablesToCoordinateSystem(coordinateSystemName, retrievedVariableNames);
        }

        if (this.state.coordinateSystem) {
            const coordinateSystem = this.state.coordinateSystem;
            const retrievedVariableNames = Object.keys(retrievedVariablesToEdit);

            const conflicts = editor.getRetrievedVariableCoordinateSystemConflicts(coordinateSystem, retrievedVariableNames);

            if (_.isEmpty(conflicts)) {
                editor.addRetrievedVariablesToCoordinateSystem(coordinateSystem, retrievedVariableNames);
                editor.commit();
                NavigationUtils.gotoRetrievalRules(this.props.process.name);
                ComponentUtils.closeImportWizardModal();

            } else {
                ComponentUtils.promptDimensionConflictResolution(editor.process,
                    coordinateSystem,
                    conflicts,
                    (resolutions) => {
                        editor.addRetrievedVariablesToCoordinateSystem(coordinateSystem, retrievedVariableNames, resolutions);
                        editor.commit();
                        NavigationUtils.gotoRetrievalRules(this.props.process.name);
                        ComponentUtils.closeImportWizardModal();
                    }
                );
            }

        } else {
            editor.commit();
            // Krista decided it is confusing to navigate to rules page after adding variables, and I agree
            // NavigationUtils.gotoRetrievalRules(this.props.process.name);
            ComponentUtils.closeImportWizardModal();
        }
    };

    addSGPCompanionRules = (editor, inputDatasetName, editInfo) => {
        const sgpDatastreamsWithMissingC1Data = Constants.SGP_DATASTREAMS_WITH_NO_C1_DATA;
        const process = editor.process;
        const newRule = editInfo.rule;

        // We have to do a special check for met.b1 and add the E13 companion rule
        const inputDataset = process.variable_retrieval.input_datasets[inputDatasetName];

        const sgpC1 = new RunLocation('SGP', 'C1');
        const sgpE13 = new RunLocation('SGP', 'E13');

        if (ProcessUtils.hasRunLocation(process, sgpC1) && sgpDatastreamsWithMissingC1Data.indexOf(newRule.datastream_name) > -1) {

            let hasE13 = false;

            if (inputDataset) { // input dataset may not exist yet if this is the first time this datastream is used
                inputDataset.rules.forEach((rule, index) => {
                    if (rule.datastream_name === newRule.datastream_name) {
                        if (sgpC1.isEqual(rule.run_location) && sgpE13.isEqual(rule.data_location)) {
                            hasE13 = true;
                        }
                    }
                });
            }

            if (!hasE13) {
                // We need to add E13 rule
                const companionRule = {
                    run_location: { site: 'SGP', fac: 'C1' },
                    run_time: { start: 'ANY_TIME', end: 'ANY_TIME' },
                    data_location: { site: 'SGP', fac: 'E13' },
                    datastream_name: newRule.datastream_name,
                    variable_mappings: editInfo.rule.variable_mappings
                };

                editor.editInputDataset(inputDatasetName, editInfo.offsets, companionRule);
            }
        }

    };

    createInputDatasetName = (selectedDod) => {
        let uniqueName = '';
        const nameBase = selectedDod.replace('.', '_');
        const inputDatasetList = Object.keys(this.props.process.variable_retrieval.input_datasets);

        if (inputDatasetList.indexOf(nameBase) === -1) {
            uniqueName = nameBase;
        } else {
            let unique = false;
            let count = 2;

            while (!unique) {
                if (inputDatasetList.indexOf(`${nameBase}_${count}`) === -1) {
                    uniqueName = `${nameBase}_${count}`;
                    unique = true;
                }
                count += 1;
            }
        }

        //more logic needed to make sure it is unique
        return uniqueName;
    };

    variablesExistCheck = () => {
        const existingVariables = [];
        const dodVars = ProcessUtils.getDatastreamVariablesForDod(this.state.selectedDod);

        const formVariables = _.clone(this.state.variableMappings);
        for (const datastreamVariable in formVariables) {
            const newRetrievedVariable = formVariables[datastreamVariable].retrievedVariableName;
            Object.keys(this.props.process.variable_retrieval.retrieved_variables).forEach((retrievedVariable) => {
                if (newRetrievedVariable === retrievedVariable) {
                    let datastreamDims = [];
                    if (dodVars[datastreamVariable]) {
                        datastreamDims = dodVars[datastreamVariable].dims;
                    }
                    existingVariables[retrievedVariable] = {
                        datastreamVariable,
                        datastreamVariableDims: datastreamDims
                    };
                }
            });
        }
        return existingVariables;
    };

    areVariableNamesValid = (validator) => {
        let valid = { valid: true, message: '' };
        Object.keys(this.state.variableMappings).forEach((datastreamVariable) => {
            const retrievedVariable = this.state.variableMappings[datastreamVariable].retrievedVariableName;
            const validationResults = validator.isRetrievedVariableNameValid(retrievedVariable, null);
            if (validationResults.message !== null) {
                valid = { valid: false, message: validationResults.message };
            }
        });

        return valid;
    };

    areDatastreamNamesValid = (validator) => {
        let valid = { valid: true, message: '' };
        this.state.outputDatastreams.forEach((datastreamName) => {
            const validationResults = validator.isDatastreamNameValid(datastreamName, null);
            if (validationResults.message !== null) {
                valid = { valid: false, message: validationResults.message };
            }
        });

        return valid;
    };

    clearErrorMessage = () => {
        if (this.errorMessageComponent !== null) {
            this.errorMessageComponent.className = '';
            this.errorMessageComponent.textContent = '';
            $('.modal-footer .btn-ok').attr('disabled', false);
        }
    };

    onOkClicked = () => {
        //clear message
        this.clearErrorMessage();

        const existingVariables = this.variablesExistCheck();
        const validator = new ProcessValidator(this.props.process);
        const namesValid = this.areVariableNamesValid(validator);
        const datastreamsValid = this.areDatastreamNamesValid(validator);
        const selectedDodValid = validator.isDatastreamNameValid(this.state.selectedDod);
        let hasError = false;

        //TODO: allow multiple validation errors to display at once and clear once fixed rather than on any change
        if (selectedDodValid.level) {
            hasError = true;
            this.errorMessageComponent.textContent = selectedDodValid.message;

        } else if (Object.keys(this.state.variableMappings).length === 0) {
            hasError = true;
            this.errorMessageComponent.textContent += 'Please select at least one variable to import.';

        } else if (!namesValid.valid) {
            hasError = true;
            this.errorMessageComponent.textContent += namesValid.message;

        } else if (validator.isPositiveInteger(this.state.startOffset) || validator.isPositiveInteger(this.state.endOffset)) {
            hasError = true;
            this.errorMessageComponent.textContent += 'Offset must be a positive integer';

        } else if (!datastreamsValid.valid) {
            hasError = true;
            this.errorMessageComponent.textContent += datastreamsValid.message;

        } else if (Object.keys(existingVariables).length === 0) {
            this.commitChanges({});

        } else {
            let formComponent = null;
            const formRefFunction = (component) => { formComponent = component; };
            const coordinateSystem = this.state.coordinateSystem ? this.state.coordinateSystem.value : null;

            const form = (
                <RetrievedVariableExistsForm
                    process={this.props.process}
                    existingVariables={existingVariables}
                    ref={formRefFunction}
                    coordinateSystem={coordinateSystem}
                />
            );

            const addOk = () => {
                if (formComponent.validateAllValues()) {
                    const existingVariablesRes = formComponent.getResolutionValues();
                    this.commitChanges(existingVariablesRes);
                    return true;
                } else {
                    return false;
                }
            };

            ModalsService.openModal({
                id: 'variable_exists_conflict',
                modalType: ModalsService.TYPE_CONFIRM,
                modalTitle: 'Variable Exists',
                modalMessage: form,
                onOk: addOk
            });
        }

        if (hasError) {
            this.errorMessageComponent.className = 'invalid';
            $('.modal-footer .btn-ok').attr('disabled', true);
        }
    };

    onCancelClicked = () => {
        ComponentUtils.closeImportWizardModal();
    };

    updateButtonState = (selectedItems) => {
        let buttonDisabled = true;
        if (Object.keys(selectedItems).length > 0) {
            buttonDisabled = false;
        }

        this.setState({ buttonDisabled });
    };

    renderDatastreamVarsList = () => {
        let datastreamVarsList = null;
        const datastreamVarsRefFunction = (component) => { this.datastreamVarsComponent = component; };

        if (this.state.selectedDod !== null) {
            const dodVars = this.getDodVars();
            if (dodVars) {
                datastreamVarsList = (
                    <DatastreamVarsList
                        id="datastreamVars"
                        sortedIds={Object.keys(dodVars).sort()}
                        data={dodVars}
                        filterable
                        onSelectionChanged={this.onDatastreamVarsChange}
                        ref={datastreamVarsRefFunction}
                        datastreamName={this.state.selectedDod}
                        onSelectionClick={this.updateButtonState}
                    />
                );
            } else {
                datastreamVarsList = <LoadingSpinner/>;
            }
        }
        return datastreamVarsList;
    };

    renderRecommendedVarList = () => {
        let datastreamList = null;
        const datastreamRefFunction = (component) => { this.datastreamRecComponent = component; };

            const vars = this.getRecommendedVariables();
            const data = {};
            vars.forEach(v => {
                data[`${v.name}-${v.dod}`] = v;
            });

            let multiSelect = false;
            if (this.state.selectedDod !== null) {
                multiSelect = true;
            }

            if (vars) {
                datastreamList = (
                    <RecommendedVariablesList
                        id="datastreamVars"
                        sortedIds={Object.keys(data).sort()}
                        data={data}
                        filterable
                        multiSelect={multiSelect}
                        onSelectionChanged={this.onRecVariableChange}
                        ref={datastreamRefFunction}
                        onSelectionClick={this.updateButtonState}
                    />
                );
            }
        
        return datastreamList;
    };

    renderSelectedDatastreamVarsTable = () => {
        const selectedDatastreamVarsRefFunction = (component) => { this.selectedDatastreamVarsRefFunction = component; };

        return (
            <ImportMappingsTable
                key="mappings-table"
                process={this.props.process}
                prefix={this.state.prefix}
                dodVariableOptions={this.getDodVars()}
                variableMappings={this.state.variableMappings}
                onAdd={this.onAddMapping}
                onDelete={this.onDeleteMapping}
                onPrefixChange={this.onPrefixChange}
                onRetrievedVariableChanged={this.onRetrievedVariableChanged}
                onVariableUnitsChanged={this.onVariableUnitsChanged}
                onOutputMappingChanged={this.onOutputMappingChanged}
                clearErrorMessage={this.clearErrorMessage}
                ref={selectedDatastreamVarsRefFunction}
                outputDatastreams={this.state.outputDatastreams}
            />
        );
    };

    renderCoordinateSystemInput = () => {
        let content = '';
        const coordinateSystemValue = ProcessUtils.getCoordinateSystemSelectValue(this.state.coordinateSystem, this.coordinateSystemOptions);

        if (this.coordinateSystemOptions.length > 1) {
            content = (
                <span>
                    <div className="section-title">Coordinate System</div>
                    <ComboBoxHOC
                        classNamePrefix="react-select"
                        value={coordinateSystemValue}
                        options={this.coordinateSystemOptions}
                        onChange={this.onCoordinateSystemChange}
                        isClearable
                        name="dodLocation"
                    />
                </span>
            );
        }

        return content;
    };

    onDimensionChange = (oldDimensionName, e) => {
        const newDimensionsState = _.clone(this.state.variableDimensions);
        newDimensionsState[oldDimensionName] = e.value;

        this.setState({ variableDimensions: newDimensionsState });
    };

    renderVariableDimensionsInput = () => {
        let dimensionOptions = ProcessUtils.getDimensionNamesAsArray(this.props.process, this.state.coordinateSystem);
        dimensionOptions = Constants.convertArrayToSelectOptions(dimensionOptions);

        const variableInputs = [];
        Object.keys(this.state.variableDimensions).forEach(dimensionName => {

            const value = Constants.convertValueToSelectValue(this.state.variableDimensions[dimensionName]);
            variableInputs.push(
                <tr key={`variable-dim-${dimensionName}`}>
                    <td className={css`vertical-align: middle !important;`}>{dimensionName}</td>
                    <td>
                        { dimensionName !== 'time' ?
                            <ComboBoxHOC
                                creatable
                                classNamePrefix="react-select"
                                value={value}
                                options={dimensionOptions}
                                onChange={(e => {
                                    this.onDimensionChange(dimensionName, e)
                                })}
                            />
                            :
                            'time'
                        }
                    </td>
                </tr>
            );
        });

        return (
            <span style={{ marginTop: '10px' }}>
                <div className="section-title">Variable Dimensions</div>
                <table className={`table table-bordered ${css`width:100%;`}`}>
                    <tbody>
                        {variableInputs}
                    </tbody>
                </table>
            </span>
        );
    };

    renderOutputDatastreamInput = () => {
        let content = '';
        const value = this.state.outputDatastreams.map(v => {
            return Constants.convertValueToSelectValue(v);
        });

        if (this.outputDatastreamOptions.length > 0) {
            content = (
                <span style={{ marginTop: '10px' }}>
                    <div className="section-title">Output Datastream</div>
                    <ComboBoxHOC
                        creatable
                        classNamePrefix="react-select"
                        value={value}
                        options={this.outputDatastreamOptions}
                        onChange={this.onOutputDatastreamChange}
                        isClearable
                        isMulti
                        name="outputDatastream"
                        style={{ marginBottom: '10px' }}
                    />
                </span>
            );
        }

        return content;
    };

    /**
     * Allow the user to select the retrieval rule set the variables will be imported into.
     * The default is AUTO-SELECT, which will use our logic to determine the best
     * input dataset to import into.  The user also has the option to select a different
     * input dataset or explicitly create a new one.
     */
    renderInputDatasetInput = () => {
        let content = '';
        const inputDatasetOptions = this.getInputDatasetOptions();
        const inputDatasetValue = ProcessUtils.getSelectedSelectValue(this.state.inputDataset, inputDatasetOptions);

        content = (
            <span style={{ marginTop: '10px' }}>
                <InfoBoxLabel label="Retrieval Rule Set" className="section-title" hideColon
                              message={InfoMessages.selectInputDataset}/>
                <ComboBoxHOC
                    creatable
                    classNamePrefix="react-select"
                    value={inputDatasetValue}
                    options={inputDatasetOptions}
                    onChange={this.onInputDatasetChange}
                    isClearable={false}
                    name="inputDataset"
                />
            </span>
        );

        return content;

    }

    renderExtraFields = () => {
        const labelStyles = css`font-weight:bold; width:95px; display:inline-block;`;
        const wrapperStyle = css`
            padding: 13px 5px 3px 13px;
            flex-shrink: 0;
        `;
        return (
            <Flexbox flexDirection="column" style={{ marginTop: '18px' }}>
                <Flexbox className={cx('section-subsection', wrapperStyle)} flexDirection={'column'}>
                    <VariableRequiredOptionsForm
                        process={this.props.process}
                        variableRequired={this.state.variableRequired}
                        retrieveQCVariable={this.state.retrieveQcVariable}
                        qcVariableRequired={this.state.qcVariableRequired}
                        onVariableRequiredChanged={this.onVariableRequiredChanged}
                        onRetrieveQCVariableChanged={this.onRetrieveQCVariableChanged}
                        onQCVariableRequiredChanged={this.onQCVariableRequiredChanged}
                        variableRequiredText={'Required?'}
                        retrieveQCVariableText={'Retrieve QC?'}
                        qcVariableRequiredText={'QC Required?'}
                        inlineStyle
                    />
                </Flexbox>
                <div className="section-title">Offsets</div>
                <div className="section-subsection">
                    <Flexbox flexDirection="row" className="offsets start">
                        <span className="offset-label">
                            <InfoIcon message={InfoMessages.inputDatasetOffsetStart} />
                            Start:
                        </span>
                        <SecondsInput
                            value={this.state.startOffset}
                            onChange={this.handleComponentInputChange}
                            fieldPath="startOffset"
                            className="seconds-input-wizard"
                        />
                    </Flexbox>
                    <Flexbox flexDirection="row" className="offsets end">
                        <span className="offset-label">
                            <InfoIcon message={InfoMessages.inputDatasetOffsetEnd} />
                            End:
                        </span>
                        <SecondsInput
                            value={this.state.endOffset}
                            onChange={this.handleComponentInputChange}
                            fieldPath="endOffset"
                            className="seconds-input-wizard"
                        />
                    </Flexbox>
                </div>
                {this.renderCoordinateSystemInput()}
                {this.renderVariableDimensionsInput()}
                {this.renderOutputDatastreamInput()}
                {this.renderInputDatasetInput()}
            </Flexbox>
        );
    };

    render() {
        const errorMessageRefFunction = (component) => { this.errorMessageComponent = component; };
        const selectedDodValue = this.state.selectedDod ? Constants.convertValueToSelectValue(this.state.selectedDod) : null;
        const selectedRecVarValue = this.state.selectedRecVar ? Constants.convertValueToSelectValue(this.state.selectedRecVar) : null;

        let dodHasVariables = true;
        let dodHasAvailableVariables = true;
        if (this.state.selectedDod !== null) {
            const dodVars = ProcessUtils.getDatastreamVariablesForDod(this.state.selectedDod);
            const dodAvailableVars = this.getDodVars();

            if (dodVars && _.isEmpty(dodVars)) {
                dodHasVariables = false;
            }

            if (dodAvailableVars && _.isEmpty(dodAvailableVars)) {
                dodHasAvailableVariables = false;
            }
        }

        const leftPanel = css`
            max-height: calc(100vh - 280px);
        `;

        const rightPanel = css`
            max-height: calc(100vh - 290px);
            width: 100%;
            flex: 1;
        `;

        const divStyle = css`
            margin-bottom: 20px;
        `;

        const anchorStyle = css`
            font-weight: bold;
            padding: 0 5px;
        `;

        const infobox = css`
            align-self: flex-start !important;
        `;

        return (
            <div className="static-modal import-vars-wizard">
                <Modal.Dialog bsSize="large" ref="dialog">
                    <Modal.Header>
                        <Modal.Title>Add Variables</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Flexbox flexDirection={'row'} className={typeSelectionStyle}>
                            <InfoIcon message={InfoMessages.importVariables}/>
                            <ToolbarItem
                                                title={<span><strong>Retrieve data by:</strong> {this.state.mode}</span>}
                                                enabled
                                                dropdownButton
                                                textOnly
                                                className={css`&:hover { cursor: pointer }`}
                            >
                                                <Menu>
                                                    <Menu.Item
                                                        key={Constants.RETRIEVE_MODE_DS}
                                                        onClick={this.onModeChange}
                                                    >
                                                        {Constants.RETRIEVE_MODE_DS}
                                                    </Menu.Item>
                                                    <Menu.Item
                                                        key={Constants.RETRIEVE_MODE_REC}
                                                        onClick={this.onModeChange}
                                                    >
                                                        {Constants.RETRIEVE_MODE_REC}
                                                    </Menu.Item>
                                                </Menu>
                                </ToolbarItem>
                            </Flexbox>
                        <Flexbox flexDirection="column" flexGrow={1} width={'100%'}>
                            <Flexbox width={'100%'}>
                                <Flexbox flexDirection="row" style={{ minHeight: '0', width: '100%' }}>
                                    <Flexbox flexDirection="column" className="column" width="500px">
                                        <Flexbox flexDirection="column" className="noshrink" flexGrow={0}>
                                            <div style={{ width: '100%', marginBottom: '10px' }}>
                                                { this.state.mode === Constants.RETRIEVE_MODE_DS &&
                                                    <ComboBoxHOC
                                                        creatable
                                                        classNamePrefix="react-select"
                                                        value={selectedDodValue}
                                                        options={this.dodSelectOptions}
                                                        onChange={this.onDatastreamChange}
                                                        isClearable={false}
                                                        isMulti={false}
                                                        placeholder="Select datastream or type in name..."
                                                    />
                                                }
                                            </div>
                                        </Flexbox>
                                        {this.state.mode == Constants.RETRIEVE_MODE_DS &&
                                        <Flexbox flexDirection="column" flexGrow={1}
                                                 className={cx('section-body', leftPanel)}>

                                            {dodHasVariables &&
                                            <div className="section-title">
                                                {this.state.selectedDod ? `${this.state.selectedDod} ` : 'Datastream '}
                                                Variables
                                            </div>
                                            }
                                            {dodHasVariables && dodHasAvailableVariables &&
                                            this.renderDatastreamVarsList()
                                            }
                                            {dodHasVariables && !dodHasAvailableVariables &&
                                            <div
                                                className={css`color:${colorTextGrey};font-style:italic;text-align:center;`}>
                                                All variables from this datastream have been imported.
                                            </div>
                                            }
                                            {!dodHasVariables &&
                                            <div
                                                className={css`color:${colorTextGrey};font-style:italic;text-align:center;`}>

                                                <div className={cx(divStyle, css`color:${colorRed}`)}>
                                                    DOD <b>{this.state.selectedDod}</b> does not exist!
                                                </div>
                                                <div className={divStyle}>For optimal PCM usability, you should create a
                                                    DOD.
                                                </div>
                                                <div className={divStyle}>
                                                    Please open the
                                                    <a className={anchorStyle} target="_blank" rel="noopener noreferrer"
                                                       onClick={NavigationUtils.openExternalLinkInNewWindowFunc(dodUrl)}>
                                                        DOD Manager
                                                    </a>
                                                    and import a NetCDF file for this datastream in order to create a
                                                    DOD.
                                                </div>
                                            </div>
                                            }
                                        </Flexbox>
                                        }
                                        {this.state.mode == Constants.RETRIEVE_MODE_REC &&
                                        <Flexbox flexDirection="column" flexGrow={1}
                                                 className={cx('section-body', leftPanel)}>

                                            <div className="section-title">
                                                Recommended Variables
                                            </div>
                                            {this.renderRecommendedVarList()}
                                            {this.state.selectedDod && 
                                            <div className={css`padding: 10px 15px;`}>
                                                <em className={css`color:${colorTextGrey}`}>only displaying variables from same datastream as current imports</em>
                                            </div>
                                            }
                                        </Flexbox>
                                        }

                                    </Flexbox>
                                    <Flexbox alignItems="center" className="column">
                                        <button
                                            className="btn btn-primary"
                                            onClick={this.onAddMappingFromListSelection}
                                            disabled={this.state.buttonDisabled}
                                        >
                                            <i className="fa fa-2x fa-arrow-right" aria-hidden/>
                                        </button>
                                    </Flexbox>
                                    <Flexbox flexDirection="column" flexGrow={2} width="100%" style={{ overflow: 'auto' }}>
                                        <Flexbox className="section-title noshrink">Import:</Flexbox>
                                        <Flexbox className={cx('section-body', 'rie-scrollable', rightPanel)} flexDirection="row" flexGrow={1}>
                                            <Flexbox flexDirection="column" style={{ marginRight: '20px' }} flexGrow={1}>
                                                {this.renderSelectedDatastreamVarsTable()}
                                            </Flexbox>
                                            {!this.props.wizardView &&
                                                this.renderExtraFields()
                                            }
                                        </Flexbox>
                                    </Flexbox>
                                </Flexbox>
                            </Flexbox>
                            <div className="error-message-container">
                                <div ref={errorMessageRefFunction} />
                            </div>
                        </Flexbox>
                    </Modal.Body>

                    <Modal.Footer>
                        <Button onClick={this.onOkClicked} type="primary" className="btn-ok">OK</Button>
                        <Button autoFocus onClick={this.onCancelClicked}>Cancel</Button>
                    </Modal.Footer>

                </Modal.Dialog>
            </div>
        );
    }
}

class RetrievedVariableExistsForm extends React.Component {
    static propTypes = {
        process: PropTypes.object,
        retrievedVariable: PropTypes.string,
        existingVariables: PropTypes.array,
        coordinateSystem: PropTypes.string
    };

    constructor(props) {
        super(props);

        let validation = {};
        Object.keys(props.existingVariables).forEach((variable) => {
            validation = Object.assign({}, validation, {
                [variable]: {
                    valid: true,
                    message: ''
                }
            });
        });

        this.state = {
            validation
        };
    }

    onInputChange = (e) => {
        const variableName = e.target.name.replace('rename', '');
        $(`input[name=handleExistingVariable${variableName}]`).filter('[value="renameVariable"]').prop('checked', 'checked');
        const validation = this.state.validation;
        validation[variableName] = { valid: true, message: '' };
        this.setState({ validation });
    };

    getResolutionValues = () => {
        let values = {};

        Object.keys(this.props.existingVariables).forEach((variable) => {
            const escapedVariable = variable.replace('@', '\\@');
            const action = $(`input[name=handleExistingVariable${escapedVariable}]:checked`).val();
            const rename = $(`input[name=rename${escapedVariable}]`).val();
            values = Object.assign({}, values, {
                [variable]: {
                    action, rename
                }
            });
        });

        return values;
    };

    validateAllValues = () => {
        let valid = true;
        const allVariablesList = Object.keys(this.props.process.variable_retrieval.retrieved_variables);
        const validator = new ProcessValidator(this.props.process);
        const validation = this.state.validation;

        const values = this.getResolutionValues();

        Object.keys(values).forEach((variableName) => {
            const variable = values[variableName];
            if (variable.action === 'renameVariable') {
                //check that name doesn't match other inputs from the form
                allVariablesList.push(variable.rename);

                const validationResults = validator.isEntityNameValid(variable.rename, allVariablesList, 'Variable');
                if (validationResults.message !== '') {
                    valid = false;

                    validation[variableName] = { valid: false, message: validationResults.message };
                }
            }
        });

        this.setState({ validation });

        return valid;
    };

    renderVariableList = () => {
        return Object.keys(this.props.existingVariables).map((variable) => {
            return <li key={`list-${variable}`}>{variable}</li>;
        });
    };

    renderError = (variable) => {
        let error = '';
        if (!this.state.validation[variable].valid) {
            error = (<span><i className="fa fa-exclamation-triangle"/>{this.state.validation[variable].message}</span>);
        }

        return error;
    };

    renderVariableForms = () => {
        const form = [];

        Object.keys(this.props.existingVariables).forEach((variable) => {
            let className = '';
            if (!this.state.validation[variable].valid) {
                className = 'invalid';
            }
            const datastreamVariableDims = this.props.existingVariables[variable].datastreamVariableDims;
            const datastreamVariable = this.props.existingVariables[variable].datastreamVariable;
            const retrievedVariableDims = this.props.process.variable_retrieval.retrieved_variables[variable].dims;

            if (_.isEqual(datastreamVariableDims, retrievedVariableDims) ||
                (this.props.coordinateSystem)) {
                // The dims are equal between the datastream variable and the retrieved varable, so we can add a rule
                form.push(
                    <div key={`form-${variable}`} className="section">
                        <p><strong>{variable}</strong></p>
                        <div>
                            <input
                                type="radio"
                                name={`handleExistingVariable${variable}`}
                                value="addRule"
                                defaultChecked
                            />
                            <span className="radio-label">Append a new retrieval rule for this variable</span>
                        </div>
                        <div>
                            <input
                                type="radio"
                                name={`handleExistingVariable${variable}`}
                                value="renameVariable"
                            />
                            <span className="radio-label">Create a new variable with unique name:</span>
                            <input
                                name={`rename${variable}`}
                                className={className}
                                type="text"
                                placeholder="Import as name..."
                                onChange={this.onInputChange}
                            />
                        </div>
                        <div className="error-message">
                            {this.renderError(variable)}
                        </div>
                    </div>
                );

            } else {
                // The dims are not equal, so we can't assign a rule to support this datastream.  Instead, it can only
                // be imported as a new variable.
                form.push(
                    <div key={`form-${variable}`} className="section">
                        <p><strong>{variable}</strong></p>
                        <div>
                            <input
                                type="radio"
                                name={`handleExistingVariable${variable}`}
                                value="renameVariable"
                                defaultChecked
                            />
                            <span className="radio-label">Create a new variable with unique name:</span>
                            <input
                                name={`rename${variable}`}
                                className={className}
                                type="text"
                                placeholder="Import as name..."
                                onChange={this.onInputChange}
                            />
                        </div>
                        <div className="warning-box">
                            <span className="warning-message">Note:</span>
                            The shape of existing variable <span className="entity-name">{variable} ({retrievedVariableDims.join(', ')})</span>
                            does not match the shape of the datastream variable <span className="entity-name">{datastreamVariable} ({datastreamVariableDims.join(', ')})</span>,
                            so this datastream variable may only be imported with a unique name.
                        </div>
                        <div className="error-message">
                            {this.renderError(variable)}
                        </div>
                    </div>
                );
            }
        });

        return form;
    };

    render = () => {
        return (
            <div className="handle-existing-variables-modal scrollable">
                <p>The following variables have already been defined for this process:</p>
                <ul>
                    {this.renderVariableList()}
                </ul>
                <p>How would you like to resolve the conflict?</p>
                <div className="form">
                    {this.renderVariableForms()}
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/page/process/variableRetrieval/ImportVariablesWizard.js